// List-row content for TaskItemBase.
// @ts-check
import { Box } from '@mui/material';
import TaskBreadcrumbs from '@/features/tasks/components/atoms/TaskBreadcrumbs';
import TaskTitleSection from '@/features/tasks/components/atoms/TaskTitleSection';
import { TaskSubtaskProgressBar } from '@/features/tasks/components/atoms/TaskSubtaskProgressBar';
import { shouldHideListCheckboxFromTabOrder } from '@/features/tasks/utils/ui/taskListItemUtils';
import { buildTaskRowTitleTrailingRowSx } from '@/components/ui/buildTaskRowTitleTrailingSx';
import { TaskItemListRail } from './TaskItemListRail';
import { TaskItemTrailingActions } from './TaskItemTrailingActions';

/**
 * @param {{
 *   task: Task,
 *   localCompleted: boolean,
 *   setLocalCompleted: (v: boolean) => void,
 *   user: any,
 *   handleToggleComplete: (task: Task, completed: boolean, context: any) => Promise<void>,
 *   activeFocusTaskId: string|null,
 *   focusStartTime: number|null,
 *   stopFocus: () => void,
 *   isSmallScreen: boolean,
 *   isExecutionView: boolean,
 *   isTouchDevice: boolean,
 *   breadcrumb: any,
 *   isFocused: boolean,
 *   displayDuration: number,
 *   showPlayButton: boolean,
 *   handleStartFocus: (id: string) => void,
 *   metadataSection: React.ReactNode,
 *   onStartAddingSubtask: (id: string) => void,
 *   handleDeleteTask: (id: string) => Promise<void>,
 *   view: string,
 *   showRowDelete?: boolean,
 *   showAddSubtaskButton?: boolean,
 *   selectedTaskIds: string[],
 *   isMultiSelectActive: boolean,
 *   onToggleInSelection: (id: string) => void
 * }} props
 */
export const renderListContent = ({
  task,
  localCompleted,
  setLocalCompleted,
  user,
  handleToggleComplete,
  activeFocusTaskId,
  focusStartTime,
  stopFocus,
  isSmallScreen,
  isExecutionView,
  isTouchDevice,
  breadcrumb,
  isFocused,
  displayDuration,
  showPlayButton,
  handleStartFocus,
  metadataSection,
  onStartAddingSubtask,
  handleDeleteTask,
  view,
  showRowDelete = false,
  showAddSubtaskButton = false,
  selectedTaskIds = [],
  isMultiSelectActive = false,
  onToggleInSelection = () => {},
}) => {
  const stopAllPropagation = (e) => e.stopPropagation();
  // Listbox rows are a single Tab stop (Today via isExecutionView; Plan/Organize/Dashboard via
  // hideCheckboxFromTabOrder) — otherwise Tab lands on each row checkbox and duplicates
  // page-tab stops in content-first order. Toggle via Space on selected task.
  const hideCheckboxFromTabOrder = shouldHideListCheckboxFromTabOrder(
    view,
    isExecutionView,
  );
  const showBreadcrumb =
    Boolean(breadcrumb) &&
    view !== 'today' &&
    view !== 'nested' &&
    view !== 'plan';

  return (
    <>
      {showBreadcrumb ? (
        <Box className="task-item__breadcrumb-line">
          <TaskBreadcrumbs breadcrumb={breadcrumb} />
        </Box>
      ) : null}
      <Box className="task-item__row">
        <TaskItemListRail
          task={task}
          isMultiSelectActive={isMultiSelectActive}
          selectedTaskIds={selectedTaskIds}
          onToggleInSelection={onToggleInSelection}
          localCompleted={localCompleted}
          setLocalCompleted={setLocalCompleted}
          user={user}
          handleToggleComplete={handleToggleComplete}
          activeFocusTaskId={activeFocusTaskId}
          focusStartTime={focusStartTime}
          stopFocus={stopFocus}
          isSmallScreen={isSmallScreen}
          isExecutionView={isExecutionView}
          hideCheckboxFromTabOrder={hideCheckboxFromTabOrder}
          isTouchDevice={isTouchDevice}
          isFocused={isFocused}
          showPlayButton={showPlayButton}
          handleStartFocus={handleStartFocus}
          stopAllPropagation={stopAllPropagation}
        />
        <Box className="task-item__content" sx={buildTaskRowTitleTrailingRowSx()}>
          <TaskTitleSection task={task} />
          {metadataSection}
        </Box>
        <TaskItemTrailingActions
          task={task}
          isSmallScreen={isSmallScreen}
          isExecutionView={isExecutionView}
          showAddSubtaskButton={showAddSubtaskButton}
          showRowDelete={showRowDelete}
          onStartAddingSubtask={onStartAddingSubtask}
          handleDeleteTask={handleDeleteTask}
        />
      </Box>
      {view === 'master' ? (
        <TaskSubtaskProgressBar task={task} displayDuration={displayDuration} />
      ) : null}
    </>
  );
};
